import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Typography, Tabs, Space } from 'antd';
import { UserOutlined, LockOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const LoginPage: React.FC = () => {
  const [form] = Form.useForm(); 
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loginType, setLoginType] = useState<'password' | 'code'>('password');
  const navigate = useNavigate();

  // ========== 密码登录 ==========
  const onPasswordLogin = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      // 后端 /token 接口已支持手机号/邮箱作为 username
     const response = await api.post('/token', new URLSearchParams({
        username: values.username,
        password: values.password,
      }), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      console.log('登录响应:', response.data);  // 调试日志
      localStorage.setItem('zhishentong_token', response.data.access_token);
      message.success('登录成功');
      window.location.href = '/dashboard';
    } catch (error: any) {
      message.error(error.response?.data?.detail || '用户名或密码错误');
    } finally {
      setLoading(false);
    }
  };

  // ========== 发送验证码 ==========
  const handleSendCode = async (target: string) => {
    console.log('handleSendCode 被调用，target:', target);
    if (!target) { message.warning('请输入手机号或邮箱'); return;
    }
    // 简单校验格式
    const isPhone = /^1\d{10}$/.test(target);
    const isEmail = /@/.test(target);
    if (!isPhone && !isEmail) {
      message.warning('请输入正确的手机号或邮箱');
      return;
    }
    const type = isPhone ? 'phone' : 'email';
    setCodeLoading(true);
    try {
      await api.post('/send-code', { target, type });
      message.success('验证码已发送');
      // 倒计时
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      message.error(error.response?.data?.detail || '发送失败');
    } finally {
      setCodeLoading(false);
    }
  };

  // ========== 验证码登录 ==========
  const onCodeLogin = async (values: { target: string; code: string }) => {
    setLoading(true);
    try {
      const response = await api.post('/login-code', {
        target: values.target,
        code: values.code,
        code_type: /^1\d{10}$/.test(values.target) ? 'phone' : 'email',
      });
      localStorage.setItem('zhishentong_token', response.data.access_token);
      message.success('登录成功');
      window.location.href = '/dashboard';
    } catch (error: any) {
      message.error(error.response?.data?.detail || '验证码错误或已过期');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card style={{ width: 420, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 8 }}>智审通</Title>
          <Text type="secondary">智能合同审核平台</Text>
        </div>

        <Tabs
          activeKey={loginType}
          onChange={(key) => setLoginType(key as 'password' | 'code')}
          style={{ marginBottom: 16 }}
        >
          {/* ---------- 密码登录 ---------- */}
          <TabPane tab="密码登录" key="password">
            <Form
              name="passwordLogin"
              onFinish={onPasswordLogin}
              autoComplete="off"
              size="large"
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入用户名/手机号/邮箱' }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="用户名 / 手机号 / 邮箱"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                >
                  登录
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          {/* ---------- 验证码登录 ---------- */}
          <TabPane tab="验证码登录" key="code">
            <Form
              form={form} 
              name="codeLogin"
              onFinish={onCodeLogin}
              autoComplete="off"
              size="large"
            >
              <Form.Item
                name="target"  
                rules={[{ required: true, message: '请输入手机号或邮箱' }]}
              >
                <Input
                  id="target"
                  prefix={<PhoneOutlined />}
                  placeholder="手机号 / 邮箱"
                />
              </Form.Item>

              <Form.Item
                name="code"
                rules={[{ required: true, message: '请输入验证码' }]}
              >
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder="验证码"
                    style={{ flex: 1 }}
                  />
                  <Button
                    type="default"
                    disabled={countdown > 0 || codeLoading}
                    onClick={() => {
                      const target = form.getFieldValue('target');
                      console.log('获取验证码按钮点击，target:', target); // 调试日志
                      if (target) handleSendCode(target);
                    }}
                    loading={codeLoading}
                  >
                    {countdown > 0 ? `${countdown}s` : '获取验证码'}
                  </Button>
                </Space.Compact>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                >
                  登录
                </Button>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>

        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Link to="/register">注册新账号</Link>
        </div>
      </Card>
     </div>
  );
};

export default LoginPage;
